import { push } from "connected-react-router";
import React, { useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as CONST from "../../../app/constants";
import {
  InternalId,
  IsInternalId,
  INTERNAL_ID_PREFIX,
  setMortgageProperty,
} from "../../../redux/slice/primaryAgencyApplicationSlice";
import {
  Header,
  Button,
  NavigationLinkButton,
  Paragraph,
  ErrorMark,
} from "../../../components/RegularComponents";
// import InputSelectForm from "../../../components/InputSelectForm";
import TopNavigationBar from "../../TopNavigationBar";
import { ERROR_STRING } from "../../../app/errors";
import { setAgencyCriminalRecordsDocuments } from "../../../redux/slice/primaryAgencyApplicationSlice";
import { 
  setCurrentAgentCriminalRecordsDocuments,
  setCurrentAgentMortgageProperty
} from "../../../redux/slice/additionalApplicationsSlice";
import {
  fileNameWithLengthLimit,
  fileTypeEnum,
  extractBase64,
  validateFileName,
  acceptedFileFormats,
  convertFileDataForUrl,
} from "../../../app/documentUploadUtils";
import { saveBusinessLicenceApplicationData } from "../../../redux/slice/businessLicenceApplicationSlice";
import {
  DocumentViewerFileNameLink,
  DownloadFileNameLink,
} from "../../../components/DocumentViewer/DocumentFileComponent";
import { fetchDocumentData } from "../../../redux/slice/supportingDocumentsSlice";
import DocumentViewer from "../../../components/DocumentViewer/DocumentViewer";
import { applicationTypeFromLicenceSubType, isCepRipExpiryYearSameAsRenewalYearForAgentApp, isRenewalTypeApplication, rjscOfficerObjFromEntityId } from "../../../redux/slice/applicationsUtils";
import { currentAgentApplicationSelector } from "../../../redux/selector/collectorApplicationSelector";
import { fullName } from "../../../app/utils";
import ReviewForm from "../../../components/ReviewForm/ReviewForm";
import "./ConfirmCriminalRecordCheck.styles.scss"
import { agencyApplicationSelector } from "../../../redux/selector/agencyApplicationSelector";

const CRC_CONFIRMATION_OPTIONS = {
  NO_CHANGE : "no-change",
  UPLOAD_FILE : "upload-file"
}

export const ConfirmCriminalRecordCheck = (props) => {
  const { isModal = false, onEditSave = () => {}, nextUrl } = props;
  const dispatch = useDispatch();
  const applicationMode = CONST.APPLICATION_MODE_RENEW;

  const isPrimaryAgencyApplication = !props.forBrokerApplication;

  const primaryAgencyApplication = useSelector(agencyApplicationSelector)
  const agentApplication = useSelector(currentAgentApplicationSelector);
  const licenceTypeInfo  = useSelector(state => {
    return state.primaryAgencyApplication?.licenceTypeDetails?.businessLicenceStatusList?.[0]
  })

  let applicationType = CONST.APPLICATION_TYPE_MORTGAGE_BROKERAGE_LICENCE
  
  if(!isPrimaryAgencyApplication && agentApplication) { 
    const { licencingSubType } = agentApplication;
    applicationType = applicationTypeFromLicenceSubType(licencingSubType);
  }

  const {crcNoChangeIds, crcUpdatedIds} = useSelector(state => {
    if (!isPrimaryAgencyApplication) {
      return {
        crcNoChangeIds : agentApplication?.businessLicenceData?.mraRenewalCrcNoChangeIds ?? null,
        crcUpdatedIds : agentApplication?.businessLicenceData?.mraRenewalCrcUpdatedIds ?? null
      }
    }

    const businessLicenceData = state.primaryAgencyApplication?.application?.businessLicenceData

    return {
      crcNoChangeIds : businessLicenceData?.mraRenewalCrcNoChangeIds ?? null,
      crcUpdatedIds : businessLicenceData?.mraRenewalCrcUpdatedIds ?? null
    }
  })

  // This will change to primaryAgent when branch is pushed
  /**
   * The list of all documents of type criminal record available in the appplication.
   * 
   * It will have all the documents from new application flow and renewal application flow.
   * 
   */
  const criminalRecordDocuments = useSelector((state) => {
    let criminalRecords = [];

    // the documents which are marked as CRIMINAL_RECORD, not removed and created after the 
    // application was approved last.
    const filterCriminalDocumentFunc = (docs) => (docs.documentType === CONST.DOCUMENT_TYPE_CRIMINAL_RECORD 
      && docs.changeType !== "DELETE"
      && state.primaryAgencyApplication.application.issuedDate < (docs.createdDate ?? Date.now()))

    if (
      isPrimaryAgencyApplication &&
      state.primaryAgencyApplication.application?.documents
    ) {
      criminalRecords =
        state.primaryAgencyApplication.application?.documents.filter(filterCriminalDocumentFunc);
    } else if (agentApplication?.documents) {
      criminalRecords = agentApplication?.documents.filter(filterCriminalDocumentFunc);
    }

    return criminalRecords;
  });

  const companyRjscDetails = useSelector(
    (state) =>
      state.primaryAgencyApplication?.application?.businessLicenceData
        ?.companyRjscDetails
  );

  const serverSaveRequest = useSelector(state => state.primaryAgencyApplication.serverRequestStatus)

  const peopleList = useMemo(() => {
    if (companyRjscDetails?.businessData?.peopleList) {
      return companyRjscDetails?.businessData?.peopleList;
    }

    return [];
  }, [companyRjscDetails]);
  
  // The existing documents
  // this will only keep the documents uploaded successfully with proper GUID value.
  /**
   * criminalRecordsMapping :  The list of documents uploaded in the renewal flow.
   *                        This field will be empty the first time but will have vlaues when in the Resume Renewal flow
   * crcCheckOption : The key-value pair object indicating the option for each entity to preselect the radio buttons
   */
  const { criminalRecordsMapping, crcCheckOption} = useMemo(() => {
    let criminalRecordsMapping = {}
    let crcCheckOption = {}

    const filteredCRCDocuments = criminalRecordDocuments.filter((doc) => {
      const person = rjscOfficerObjFromEntityId(peopleList, doc["entityId"])
      if(!person) {
        return false;
      }
      return true;
    });

   criminalRecordsMapping = filteredCRCDocuments.reduce((acc, doc) => {

     if(acc.hasOwnProperty([doc["entityId"]])) {
        // document is already present
        const presentDoc = acc[doc["entityId"]]
        const create1 = presentDoc.createdDate ?? Date.now()
        const create2 = doc.createdDate ?? Date.now()

        if(create1 > create2) {
          // document in the object is latest
          return acc
        } else if(presentDoc.id > doc.id) {
          return acc
        }
     }

     return {...acc,[doc["entityId"]] : doc}
    }, {})
   
   crcNoChangeIds?.split(",").forEach(id => {
    crcCheckOption[id] = CRC_CONFIRMATION_OPTIONS.NO_CHANGE
   })

   crcUpdatedIds?.split(",").forEach(id => {
    crcCheckOption[id] = CRC_CONFIRMATION_OPTIONS.UPLOAD_FILE
   })

  
    return {
      criminalRecordsMapping,
      crcCheckOption
    }
  }, [criminalRecordDocuments, crcNoChangeIds, crcUpdatedIds, peopleList])

  const [showValidationError, setShowValidationError] = useState(false);
  const [applicationSaveError, setApplicationSaveError] = useState(false);

  const [openDocumentViewer, setOpenDocumentViewer] = useState(false);
  const [documentViewerDocument, setDocumentViewerDocument] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);

  // saves the radio button selection for each entity
  const [crcConfirmationOption, setCrcConfirmationOption] = useState(crcCheckOption)
  
  // the state object to save the new crc uploaded for the missing employees
  // and any update to the existing employees
  const [updatedCrc, setUpdatedCrc] = useState(criminalRecordsMapping)

  const [documentsRemoved, setDocumentsRemoved] = useState([])

  const isPersonDataValid = (people) => {
    const { entityId } = people
    let validation = true
    if(!crcConfirmationOption.hasOwnProperty(entityId)) {
      // no option selected!
      validation = false
    }

    if(crcConfirmationOption[entityId] === CRC_CONFIRMATION_OPTIONS.UPLOAD_FILE) {
        if(!updatedCrc.hasOwnProperty(entityId) || 
            updatedCrc[entityId].changeType === "DELETE") {
              // upload-file selected, but no file uploaded
              validation = false
          }
    }
    return validation
  }
  /**
   * The validation is only for the CRC which are available from past.
   
   * User has to select either "no change" or upload updated CRC for the 
   * employees for whom CRC was submitted earlier.
   */
  const validateData = () => {
    setShowValidationError(false)

    let validation = true
    
    if(isPrimaryAgencyApplication) {
      for(const people of peopleList) {
        validation = isPersonDataValid(people)
        if(!validation) {
          setShowValidationError(true)
          break
        }
      }
    } else {
      // validation for the agent
      const entityId = `${INTERNAL_ID_PREFIX}-broker`
     if (crcConfirmationOption.hasOwnProperty(entityId)) {
       if (crcConfirmationOption[entityId] === CRC_CONFIRMATION_OPTIONS.UPLOAD_FILE) {
          if(!updatedCrc.hasOwnProperty(entityId) || 
            updatedCrc[entityId].changeType === "DELETE") {
              // upload-file selected, but no file uploaded
              setShowValidationError(true)
              validation = false
          }
       }
     } else {
        setShowValidationError(true)
        validation = false;
     }
    }

    return validation;

    // const updatedCrcCount = Object.keys(updatedCrc).length;
    
    // let noChangeOptionCount = 0;
    
    // Object.keys(crcConfirmationOption).forEach(entityId => {
    //   if (crcConfirmationOption[entityId] === CRC_CONFIRMATION_OPTIONS.NO_CHANGE) 
    //     noChangeOptionCount++
    // })

    // if(noChangeOptionCount + updatedCrcCount < Object.keys(crcDataStore).length) {
    //   setErrorString(ERROR_STRING.AT_LEAST_ONE_FILE_MISSING_ERROR)
    //   return false
    // }

    // return true
  }

  const validateAndSaveDataToStore = () => {
    if (!validateData()) {
      return false;
    }

    const updatedDocuments = Object.values(updatedCrc)

    // replace the object if its available in the record
    let crCheckOnRecord = [...criminalRecordDocuments]
    updatedDocuments.forEach(document => {
      const docIndex = crCheckOnRecord.findIndex(d => d.id === document.id)
      if(docIndex >= 0) {
        crCheckOnRecord.splice(docIndex,1)
      }
    })

    const isDocumentRemoved = (document) => (documentsRemoved.findIndex(
        d => d.id === document.id) !== -1
      )

    // filter out the documents which are removed as it will be 
    // sent by a separate array
    if(isPrimaryAgencyApplication) {
      crCheckOnRecord = crCheckOnRecord.filter(d => (d.id 
        && !IsInternalId(d.id) 
        && !isDocumentRemoved(d)))
    } else {
      crCheckOnRecord = crCheckOnRecord.filter(d => (!isDocumentRemoved(d)))
    }
    
    const noChangeCrcIds = []
    const updatedCrcIds = []

    for(const [key, value]  of Object.entries(crcConfirmationOption)) {
      if(value === CRC_CONFIRMATION_OPTIONS.NO_CHANGE) {
        noChangeCrcIds.push(key)
      } else if(value === CRC_CONFIRMATION_OPTIONS.UPLOAD_FILE) {
        updatedCrcIds.push(key)
      }
    }

    if(isPrimaryAgencyApplication) {
      dispatch(setMortgageProperty({
        key : "mraRenewalCrcNoChangeIds",
        value : noChangeCrcIds.join(',')
      }))
  
      dispatch(setMortgageProperty({
        key : "mraRenewalCrcUpdatedIds",
        value : updatedCrcIds.join(',')
      }))

      dispatch(
        setAgencyCriminalRecordsDocuments(
          [
            ...crCheckOnRecord,
            ...updatedDocuments.filter(doc => doc.changeType !== "DELETE"),
            ...documentsRemoved
          ]
        )
      )
    } else {
      dispatch(setCurrentAgentMortgageProperty({
        key : "mraRenewalCrcNoChangeIds",
        value : noChangeCrcIds.join(',')
      }))

      dispatch(setCurrentAgentMortgageProperty({
        key : "mraRenewalCrcUpdatedIds",
        value : updatedCrcIds.join(',')
      }))

      dispatch(
        setCurrentAgentCriminalRecordsDocuments(
          [
            ...crCheckOnRecord,
            ...updatedDocuments.filter(doc => doc.changeType !== "DELETE"),
            ...documentsRemoved
          ]
        )
      )
    }

    // !isPrimaryAgencyApplication
    //   ? dispatch(
    //       setCurrentAgentCriminalRecordsDocuments(
    //         [
    //           ...crCheckOnRecord,
    //           ...updatedDocuments
    //         ]
    //       )
    //     )
    //   : dispatch(
    //     setAgencyCriminalRecordsDocuments(
    //       [
    //         ...crCheckOnRecord,
    //         ...updatedDocuments
    //       ]
    //     )
    //   );

      return true
  }


  const handleSaveAndNext = () => {
    if (!validateAndSaveDataToStore()) {
      return;
    }

    let nextPath = nextUrl;

    if (!nextPath) {
      if (applicationType === CONST.APPLICATION_TYPE_MORTGAGE_BROKERAGE_LICENCE) {
        nextPath = `${CONST.ROUTE_PATH.MRA_APP}${CONST.ROUTE_PATH.MRA_RENEWAL_APPLICATION_REVIEW}`; // agency
      } else {
        //MRA-1534 - point 5
        // If within the year is same as renewal year, then skip the education confirmation screen as well and directly go to
        //  regulation confirmation screen as the education requirement is already fulfilled for that year.
        const isSameYear = isCepRipExpiryYearSameAsRenewalYearForAgentApp(agentApplication, licenceTypeInfo)
        if (isRenewalTypeApplication(agentApplication) 
          && agentApplication?.cepRipExpiryDate 
          && !isSameYear
        ) {
          nextPath = `${CONST.ROUTE_PATH.MRA_APP}${CONST.ROUTE_PATH.MRA_RENEWAL_PRINCIPAL_BROKER_ACT_REGULATION_CONFIRMATION}`;
        } else {
          if (applicationType === CONST.APPLICATION_TYPE_MORTGAGE_ASSOCIATE_BROKER_LICENCE) {
            // Associate broker does not have education screen, so directly go to the regulation screen
            // even if the enum name suggests some screen for principal broker, its a generic screen for
            // all individual mortgage applications.

            // MRA-1542 : The renewal type application should have the education confirmation screen.
            if (isRenewalTypeApplication(agentApplication)) {
              nextPath = `${CONST.ROUTE_PATH.MRA_APP}${CONST.ROUTE_PATH.MRA_RENEWAL_BROKER_CONTINUING_EDUCATION_CONFIRMATION}`;
            } else {
              nextPath = `${CONST.ROUTE_PATH.MRA_APP}${CONST.ROUTE_PATH.MRA_RENEWAL_PRINCIPAL_BROKER_ACT_REGULATION_CONFIRMATION}`;
            }
          } else if (
            applicationType === CONST.APPLICATION_TYPE_MORTGAGE_PRINCIPAL_BROKER_LICENCE ||
            applicationType === CONST.APPLICATION_TYPE_MORTGAGE_INDIVIDUAL_BROKER_LICENCE
          ) {
            nextPath = `${CONST.ROUTE_PATH.MRA_APP}${CONST.ROUTE_PATH.MRA_RENEWAL_BROKER_CONTINUING_EDUCATION_CONFIRMATION}`;
          }
        }
      }
    }

    dispatch(saveBusinessLicenceApplicationData(applicationType, applicationMode))
      .then((data) => {
        // On to the next item
        dispatch(push(nextPath));
      })
      .catch((err) => {
        setApplicationSaveError(true);
      });
  };

  const handleSaveandClose = () => {
    if (!validateAndSaveDataToStore()) { return }
    onEditSave();
  };

  const fileSizeReplacement = (string, fileSize) => {
    const replaceRegExp = /<file_size>/g;
    const errorString = string.replace(replaceRegExp, fileSize + "MB");
    return errorString;
  };

  /**
   * 
   * @param {the entityId of the person} entityId 
   * 
   * This function will only set "changeType" field 
   * to DELETE. The actual change should be saved with the
   * document object at the time of continue
   */
  const handleFileRemovalForEntityId = (entityId) => {
    const updatedCrcDocument = {...updatedCrc[entityId], changeType : "DELETE"}

    const updated = Object.defineProperty(
      {...updatedCrc},
      entityId,
      {value : updatedCrcDocument, enumerable: true, writable: true}
    )
    setUpdatedCrc(updated)
    
    setDocumentsRemoved([...documentsRemoved, updatedCrcDocument])
  }

  const fileComponent = (id, file, index) => {
    return (
      <>
        <div className="supportingDocumentsFileContentWrapper">
          <span className="fileName">
            {file.documentContentType === CONST.DOCUMENT_CONTENT_TYPE_PDF ? (
              <DocumentViewerFileNameLink
                id={"supporting-document-view-file-" + file.filename}
                key={"supporting-document-view-file-" + file.filename}
                file={file}
                onClick={() => {
                  if (file.data) {
                    let fileDataUrl = convertFileDataForUrl(
                      file.data,
                      file.documentContentType
                    );
                    setDocumentViewerDocument(fileDataUrl);
                    setOpenDocumentViewer(true);
                    setSelectedFile(file);
                  } else {
                    // download data and then view it.
                    if (file.guid) {
                      dispatch(fetchDocumentData(file.guid))
                        .then((downloadFile) => {
                          if (downloadFile) {
                            let fileDataUrl = convertFileDataForUrl(
                              downloadFile.data,
                              downloadFile.documentContentType
                            );
                            setDocumentViewerDocument(fileDataUrl);
                            setOpenDocumentViewer(true);
                            setSelectedFile(downloadFile);
                          }
                        })
                        .catch((error) => {
                          console.error(error);
                        });
                    }
                  }
                }}
              />
            ) : (
              <DownloadFileNameLink
                id={"supporting-document-download-file-" + file.filename}
                componentKey={
                  "supporting-document-download-file-" + file.filename
                }
                file={file}
              />
            )}
          </span>

          <NavigationLinkButton
            id={id + "-remove-link"}
            text={"- Remove this file"}
            onClick={() => {
              handleFileRemovalForEntityId(file.entityId)
            }}
          />
        </div>
      </>
    );
  };

  const handleOptionChange = (entityId, option) => {

    if(option === CRC_CONFIRMATION_OPTIONS.NO_CHANGE && updatedCrc.hasOwnProperty(entityId)) {
      handleFileRemovalForEntityId(entityId)
    }

    const updatedOptions = Object.defineProperty(
      {...crcConfirmationOption},
      entityId,
      {value: option, enumerable: true, writable: true})
  
    setCrcConfirmationOption(updatedOptions)

    setShowValidationError(false)
  }

  const personDetails = (person) => {
    return {
      "Name" : fullName(person),
      "Relationship" : person.position,
    };
  }

  const CRCBlock = (element, index) => {
    const _uid = `mra-renewal-confirm-crc-entity-${element.entityId}`
    const person = rjscOfficerObjFromEntityId(peopleList, element.entityId)

    if(!person) {
      return null
    }

    return (
      <div className="crcCheckItem" key={_uid}>
        <div className="row">
            <div className="col-lg-12">
              <ReviewForm data={personDetails(person)} />
            </div>
          </div>
          <div className="row">
            <div className="col-lg-8">
              <b>Confirm CRC status:</b>
            </div>
          </div>
          <div className="row" style={{marginLeft: "25px", marginTop: "10px"}}>
            <div className="col-lg-8">
              <div
                className={
                  "ns-fieldset" +
                  ((showValidationError && !isPersonDataValid(person)) ? " has-error ns-fieldset-message-error" : "")
                }
              >
                <fieldset className="ns-fieldset">
                  <div className={"ns-radios ns-radios--large"}>
                    <div 
                        key={`${_uid}-no-change`} 
                        className="ns-radios__item" style={{marginBottom: "15px"}}
                    >
                      <input
                        className="ns-radios__input"
                        key={`${_uid}-no-change-input`}
                        type={"radio"}
                        value={CRC_CONFIRMATION_OPTIONS.NO_CHANGE}
                        checked={crcConfirmationOption[element.entityId] === CRC_CONFIRMATION_OPTIONS.NO_CHANGE}
                        onChange={(e) => {handleOptionChange(element.entityId, CRC_CONFIRMATION_OPTIONS.NO_CHANGE)}}
                        id={`${_uid}-no-change-input`}
                        name={`${_uid}-group`}
                      />
                      <label
                          key={`${_uid}-no-change-label`}
                          id={`${_uid}-no-change-label`}
                          className="ns-radios__label"
                          htmlFor={`${_uid}-no-change-input`}
                          onClick={() => {
                            handleOptionChange(element.entityId, CRC_CONFIRMATION_OPTIONS.NO_CHANGE) 
                          }}
                      >
                        <span className="ns-label-span">
                            No change to CRC or not required to upload CRC
                        </span>
                      </label>
                    </div>
                    <div 
                        key={`${_uid}-upload-file`} 
                        className="ns-radios__item" style={{marginBottom: "15px"}}
                    >
                      <input
                        className="ns-radios__input"
                        key={`${_uid}-upload-file-input`}
                        type={"radio"}
                        value={CRC_CONFIRMATION_OPTIONS.UPLOAD_FILE}
                        checked={crcConfirmationOption[element.entityId] === CRC_CONFIRMATION_OPTIONS.UPLOAD_FILE}
                        onChange={(e) => {handleOptionChange(element.entityId, CRC_CONFIRMATION_OPTIONS.UPLOAD_FILE)}}
                        id={`${_uid}-upload-file-input`}
                        name={`${_uid}-group`}
                      />
                      <label
                          key={`${_uid}-upload-file-label`}
                          id={`${_uid}-upload-file-label`}
                          className="ns-radios__label"
                          htmlFor={`${_uid}-upload-file-input`}
                          onClick={() => {
                            handleOptionChange(element.entityId, CRC_CONFIRMATION_OPTIONS.UPLOAD_FILE)  
                          }}
                      >
                        <span className="ns-label-span">
                        Upload Criminal Record Check (new or updated)
                        </span>
                      </label>
                    </div>
                    {crcConfirmationOption[element.entityId] === CRC_CONFIRMATION_OPTIONS.UPLOAD_FILE && 
                    <div className={"ns-fieldset"} style={{ marginLeft: "20px", paddingLeft: "55px", borderLeft: "3px solid darkgray"}}>
                      <div className="ns-file-input">
                        <div className="row">
                          <div className="col-xs-12">Upload CRC</div>
                        </div>
                        {(updatedCrc[element.entityId] && updatedCrc[element.entityId].changeType !== "DELETE") ? 
                          fileComponent(_uid,updatedCrc[element.entityId],index) : 
                          <div className="row">
                            <div className="col-xs-8">
                              <label
                                className="btn btn-primary"
                                id={`${_uid}-input-label`}
                                htmlFor={`${_uid}-input`}
                              >
                                Choose File
                              </label>
                              <input
                                className="ns-input"
                                style={{display: "none"}}
                                type="file"
                                id={`${_uid}-input`}
                                name={`${_uid}-input`}
                                accept={acceptedFileFormats(
                                  CONST.DOCUMENT_TYPE_CRIMINAL_RECORD
                                ).join(",")}
                                multiple={false}
                                value=""
                                onInput={(e) => {
                                  if (e.target.files.length > 0) {
                                    const file = e.target.files[0];

                                    // individual file size can not be more than 15 MB
                                    if (
                                      file.size >
                                      CONST.individualFileSizeAllowedInMB * 2 ** 20
                                    ) {
                                      alert(
                                        fileSizeReplacement(
                                          ERROR_STRING.SUPPORTING_DOC_VERY_LARGE_INDIVIDUAL_FILE,
                                          CONST.individualFileSizeAllowedInMB
                                        )
                                      );
                                      return;
                                    } else if (!validateFileName(file.name)) {
                                      alert(
                                        "File name is not valid. Please select another file."
                                      );
                                      return;
                                    }

                                    const reader = new FileReader();
                                    reader.onload = (readerData) => {
                                      const fileObj = {
                                        filename: fileNameWithLengthLimit(
                                          file.name
                                        ),
                                        documentContentType: fileTypeEnum(
                                          file.type
                                        ),
                                        data: extractBase64(
                                          readerData.target.result
                                        ),
                                        changeType: "SAVE",
                                        documentType:
                                          CONST.DOCUMENT_TYPE_CRIMINAL_RECORD,
                                        fileSize: file.size,
                                        entityId: element.entityId,
                                        id: InternalId()
                                      };

                                      let updatedRecords = {...updatedCrc}
                                      Object.defineProperty(
                                        updatedRecords,
                                        element.entityId,
                                        {
                                          value: fileObj,
                                          writable: true,
                                          enumerable: true
                                        }
                                      )
                                      setUpdatedCrc(updatedRecords);
                                    };

                                    reader.readAsDataURL(file);

                                    // reset validation errors
                                    setShowValidationError(false);
                                  }
                                }}
                              />
                            </div>
                            <div style={{ paddingTop: "5px"}} className="col-xs-7">
                              Accepted file types : JPG, PNG, GIF, PDF. Maximum file size : 5<abbr title="Mega Bytes">MB</abbr>.
                            </div>
                          </div>
                        }
                      </div>
                    </div>}
                  </div>
                </fieldset>
              </div>
            </div>
          </div>
      </div>
    )
  }

  const CriminalRecordBlockForPB = ({isPrincipalBroker}) => {
    const pbName = fullName(primaryAgencyApplication?.agencyApplication?.businessLicenceData?.personData)
    const agentName = fullName(agentApplication?.businessLicenceData?.personData)

    const entityId = `${INTERNAL_ID_PREFIX}-broker`;
    const _uid = `mra-renewal-confirm-crc-entity-pb`

    return (
      <div>
        <div className="row" style={{marginLeft: "25px", marginTop: "10px"}}>
          <div className="col-lg-8">
            <div
                  className={
                    "ns-fieldset" +
                    (showValidationError ? " has-error ns-fieldset-message-error" : "")
                  }
                >
              <fieldset className="ns-fieldset">
              <div className={"ns-radios ns-radios--large"}>
                <div 
                    key={`${_uid}-no-change`} 
                    className="ns-radios__item" style={{marginBottom: "15px"}}
                >
                  <input
                    className="ns-radios__input"
                    key={`${_uid}-no-change-input`}
                    type={"radio"}
                    value={CRC_CONFIRMATION_OPTIONS.NO_CHANGE}
                    checked={crcConfirmationOption[entityId] === CRC_CONFIRMATION_OPTIONS.NO_CHANGE}
                    onChange={(e) => {handleOptionChange(entityId, CRC_CONFIRMATION_OPTIONS.NO_CHANGE)}}
                    id={`${_uid}-no-change-input`}
                    name={`${_uid}-group`}
                  />
                  <label
                      key={`${_uid}-no-change-label`}
                      id={`${_uid}-no-change-label`}
                      className="ns-radios__label"
                      htmlFor={`${_uid}-no-change-input`}
                      onClick={() => {
                        handleOptionChange(entityId, CRC_CONFIRMATION_OPTIONS.NO_CHANGE) 
                      }}
                  >
                    <span className="ns-label-span">
                      {`I ${!isPrincipalBroker ? `, ${pbName},` : ``} confirm there have not been` + 
                        ` any changes to the previous criminal record check ` + 
                        `I submitted to the Registrar ${!isPrincipalBroker ? ` for ${agentName}` : ``}.`}
                    </span>
                  </label>
                </div>
                <div 
                    key={`${_uid}-upload-file`} 
                    className="ns-radios__item" style={{marginBottom: "15px"}}
                >
                  <input
                    className="ns-radios__input"
                    key={`${_uid}-upload-file-input`}
                    type={"radio"}
                    value={CRC_CONFIRMATION_OPTIONS.UPLOAD_FILE}
                    checked={crcConfirmationOption[entityId] === CRC_CONFIRMATION_OPTIONS.UPLOAD_FILE}
                    onChange={(e) => {handleOptionChange(entityId, CRC_CONFIRMATION_OPTIONS.UPLOAD_FILE)}}
                    id={`${_uid}-upload-file-input`}
                    name={`${_uid}-group`}
                  />
                  <label
                      key={`${_uid}-upload-file-label`}
                      id={`${_uid}-upload-file-label`}
                      className="ns-radios__label"
                      htmlFor={`${_uid}-upload-file-input`}
                      onClick={() => {
                        handleOptionChange(entityId, CRC_CONFIRMATION_OPTIONS.UPLOAD_FILE)  
                      }}
                  >
                    <span className="ns-label-span">
                      {`I ${!isPrincipalBroker ? `, ${pbName},` : ``} confirm there have been changes to the previous criminal record checks ${!isPrincipalBroker ? `for ${agentName}` : ``} submitted to the Registrar.`}
                    </span>
                  </label>
                </div>
              </div>
              {crcConfirmationOption[entityId] === CRC_CONFIRMATION_OPTIONS.UPLOAD_FILE && 
              <div className={"ns-fieldset"} style={{ marginLeft: "20px", paddingLeft: "55px", borderLeft: "3px solid darkgray"}}>
                <div className="ns-file-input">
                  <div className="row">
                    <div className="col-xs-12">Upload your updated Criminal Record Check:</div>
                  </div>
                  {(updatedCrc[entityId] && updatedCrc[entityId].changeType !== "DELETE") ?
                   fileComponent(_uid,updatedCrc[entityId],0)
                   :
                    <div className="row">
                      <div className="col-xs-8">
                        <label
                          className="btn btn-primary"
                          id={`${_uid}-input-label`}
                          htmlFor={`${_uid}-input`}
                        >
                          Choose File
                        </label>
                        <input
                          className="ns-input"
                          style={{display: "none"}}
                          type="file"
                          id={`${_uid}-input`}
                          name={`${_uid}-input`}
                          accept={acceptedFileFormats(
                            CONST.DOCUMENT_TYPE_CRIMINAL_RECORD
                          ).join(",")}
                          multiple={false}
                          value=""
                          onInput={(e) => {
                            if (e.target.files.length > 0) {
                              const file = e.target.files[0];

                              // individual file size can not be more than 15 MB
                              if (
                                file.size >
                                CONST.individualFileSizeAllowedInMB * 2 ** 20
                              ) {
                                alert(
                                  fileSizeReplacement(
                                    ERROR_STRING.SUPPORTING_DOC_VERY_LARGE_INDIVIDUAL_FILE,
                                    CONST.individualFileSizeAllowedInMB
                                  )
                                );
                                return;
                              } else if (!validateFileName(file.name)) {
                                alert(
                                  "File name is not valid. Please select another file."
                                );
                                return;
                              }

                              const reader = new FileReader();
                              reader.onload = (readerData) => {
                                const fileObj = {
                                  filename: fileNameWithLengthLimit(
                                    file.name
                                  ),
                                  documentContentType: fileTypeEnum(
                                    file.type
                                  ),
                                  data: extractBase64(
                                    readerData.target.result
                                  ),
                                  changeType: "SAVE",
                                  documentType:
                                    CONST.DOCUMENT_TYPE_CRIMINAL_RECORD,
                                  fileSize: file.size,
                                  entityId: entityId,
                                  id: InternalId()
                                };

                                let updatedRecords = {...updatedCrc}
                                Object.defineProperty(
                                  updatedRecords,
                                  entityId,
                                  {
                                    value: fileObj,
                                    writable: true,
                                    enumerable: true
                                  }
                                )
                                setUpdatedCrc(updatedRecords);
                              };

                              reader.readAsDataURL(file);

                              // reset validation errors
                              setShowValidationError(false);
                            }
                          }}
                        />
                      </div>
                      <div style={{ paddingTop: "5px"}} className="col-xs-7">
                        Accepted file types : JPG, PNG, GIF, PDF. Maximum file size : 5<abbr title="Mega Bytes">MB</abbr>.
                      </div>
                    </div>
                  }
                </div>
              </div>}
            </fieldset>
            </div>
          </div>
        </div>
      </div>
    )
  }

  const CriminalRecordBlock = () => peopleList.map(CRCBlock)

  const getBrokersCRCGuidelines = ()=> {
    if(!(agentApplication.licencingSubType === CONST.LICENCE_SUB_TYPE_ASSOCIATE_MORTGAGE_BROKER_RENEWAL_APPLICATION
      || agentApplication.licencingSubType === CONST.LICENCE_SUB_TYPE_MORTGAGE_BROKER_RENEWAL_APPLICATION))
    {
      return null;
    }

    let userTypeText;
    if(agentApplication.licencingSubType === CONST.LICENCE_SUB_TYPE_ASSOCIATE_MORTGAGE_BROKER_RENEWAL_APPLICATION) {
      userTypeText = "the Associate Mortgage Broker's"
    }
    else if(agentApplication.licencingSubType !== CONST.LICENCE_SUB_TYPE_MORTGAGE_BROKER_RENEWAL_APPLICATION) {
       userTypeText = "the Mortgage Broker's"
    }
    else {
      userTypeText = "a person's"
    }
    return (
      <Paragraph 
          id="crc-check-header"
          text={[
            `If there have been any changes to ${userTypeText} criminal record since their last criminal record check (CRC) was submitted to the Registrar, you must submit a new CRC.`,
            "Criminal record checks must have been applied for in person at an RCMP, municipal or military police office that is dated within last 3 months. Criminal record checks will not be accepted from third party providers."
          ]}
      />
    )
  }

  const renderHeadingAndText = () => {
    if(isPrimaryAgencyApplication) {

      const licencingSubType =
        primaryAgencyApplication?.agencyApplication?.licencingSubType;
      let isNonBrokerageApp = false;
      if (
        licencingSubType === CONST.LICENCE_SUB_TYPE_MORTGAGE_ADMINISTRATOR_RENEWAL_APPLICATION ||
        licencingSubType === CONST.LICENCE_SUB_TYPE_MORTGAGE_LENDER_RENEWAL_APPLICATION
      ) {
        isNonBrokerageApp = true;
      }

      return (
        <div className="row">
          <div className="col-xs-12 col-lg-8">
          <Header
            style={{ paddingBottom: "30px" }}
            id="crc-check-header"
            header="Criminal record check"
          />
          <Paragraph 
            id="crc-check-text-01"
            text={[
              // @no-multi-str
              "To be approved for a licence, the following individuals must <b>submit a criminal record check (CRC)</b> from the Royal Canadian Mounted Police, " + 
              "a Canadian municipal police force, or the Canadian military police, that is dated no earlier than 3 months before the " + 
              "date of application."
            ]}
          />
          <ul>
            <li>The {isNonBrokerageApp ? "Compliance Officer" : "Principal Broker"} of your business</li>
            <li>Each officer if your business is a corporation</li>
            <li>Each officer or each general partner if your business is a limited partnership</li>
            <li>Each partner if your business is a partnership</li>
            <li>Each officer of the corporation if your business is a partner that is a corporation</li>
            <li>Each sole proprietor if your business is a sole proprietorship</li>
          </ul>
          <Paragraph 
            id="crc-check-text-02"
            text={[
              // @no-multi-str
              "Criminal record check will not be accepted from third party providers.",
              "If you have previously submitted a CRC for someone, you must confirm if " + 
              "there have been <b>any changes</b> to that person’s criminal record since their " +
              "last CRC was submitted to the Registrar.  If there have been changes to " +
              "their criminal record, you must <b>submit a new CRC</b>.",
              "If there is a new officer listed below, you are required to provide a CRC for them.",
              "Please note:"
            ]}
          />
          <ul>
            <li><i>We do not require a CRC for your Recognized Agent – please choose "No change to CRC or not required to upload CRC"</i></li>
            <li><i>One individual may hold multiple roles with RJSC (ie. the same person is listed as both an officer and director). 
              You only need to provide one CRC for an individual.  If there has been no change to their CRC in the previous year, select – 
              "No change to CRC or not required to upload CRC"</i></li>
          </ul>
        </div>
      </div>
      )
    } else {
      if(agentApplication 
          && (agentApplication.licencingSubType === 
                CONST.LICENCE_SUB_TYPE_PRINCIPAL_BROKER_RENEWAL_APPLICATION 
                || agentApplication.licencingSubType === 
                CONST.LICENCE_SUB_TYPE_MORTGAGE_BROKER_RENEWAL_APPLICATION
                || agentApplication.licencingSubType === 
                CONST.LICENCE_SUB_TYPE_ASSOCIATE_MORTGAGE_BROKER_RENEWAL_APPLICATION
                 )) {
                  let headerText
                  if(agentApplication.licencingSubType === 
                    CONST.LICENCE_SUB_TYPE_PRINCIPAL_BROKER_RENEWAL_APPLICATION ) {
                      headerText = "Principal Broker Criminal Record Check"
                    }
                    else if(agentApplication.licencingSubType === 
                      CONST.LICENCE_SUB_TYPE_MORTGAGE_BROKER_RENEWAL_APPLICATION){
                      headerText = "Mortgage Broker Criminal Record Check"
                    }
                    else {
                      headerText = "Associate Mortgage Broker Criminal Record Check"
                    }

        return (
          <div className="row">
            <div className="col-xs-12 col-lg-8">
              <Header
                id="crc-check-header"
                header={headerText}
              />
              {getBrokersCRCGuidelines()}
            </div>
          </div>
        )
      }
    }
  }

  const renderSubLabel = () => {
    if(isPrimaryAgencyApplication) {
      return (
        <div className="row">
            <div className="col-xs-12 col-lg-8">
              <h2>Individuals listed under RJSC</h2>
            </div>
          </div>
      )
    } else if(agentApplication) {
      return (
        <div className="row">
            <div className="col-xs-12 col-lg-8">
              <h4>Select an option</h4>
            </div>
          </div>
      )
    }
  }

  const renderFileUploadControls = () => {
    if(isPrimaryAgencyApplication) {
      return (
        <div>
          <CriminalRecordBlock />
        </div>
      )
    } else if(agentApplication) {

      if(agentApplication.licencingSubType === CONST.LICENCE_SUB_TYPE_PRINCIPAL_BROKER_RENEWAL_APPLICATION
        || agentApplication.licencingSubType === CONST.LICENCE_SUB_TYPE_MORTGAGE_BROKER_RENEWAL_APPLICATION
        || agentApplication.licencingSubType === CONST.LICENCE_SUB_TYPE_ASSOCIATE_MORTGAGE_BROKER_RENEWAL_APPLICATION
      ) {
          return (
            <div>
              <CriminalRecordBlockForPB 
                isPrincipalBroker={(agentApplication.licencingSubType === CONST.LICENCE_SUB_TYPE_PRINCIPAL_BROKER_RENEWAL_APPLICATION)} 
              />
            </div>
          )
      }

      return (
        <div>
          <CriminalRecordBlock />
        </div>
      )
    }

    return null
  }

  return (
    <>
      <div className={!isModal ? "container" : ""}>
        {!isModal && <TopNavigationBar  {...props} applicationType={applicationType} applicationMode={applicationMode} />}
        {!isModal && renderHeadingAndText()}
        {renderSubLabel()}
        {renderFileUploadControls()}

        <div className="row topMarginLarge">
          <div
            className={
              "col-lg-4" + (showValidationError || applicationSaveError)
                ? " has-error"
                : ""
            }
          >
            <Button
              id="company-fiscal-end-date-button"
              style={{ margin: "0 10px 10px 0" }}
              buttonType={"regular_primary"}
              text={!isModal ? "Save and next" : "Save and close"}
              showSpinner={!applicationSaveError && 
                            (serverSaveRequest === CONST.REQUEST_STATUS.IN_PROGRESS)}
              onClick={!isModal ? handleSaveAndNext : handleSaveandClose}
              disabled={(serverSaveRequest === CONST.REQUEST_STATUS.IN_PROGRESS)}
            />
            {(applicationSaveError || showValidationError) && ErrorMark()}
            {showValidationError && (
              <div className="ns-error-message">
                <span className="ns-visually-hidden">Error:</span>
                {ERROR_STRING.INCOMPLETE_DATA}
              </div>
            )}
            {applicationSaveError && (
              <div className="ns-error-message">
                <span className="ns-visually-hidden">Error:</span>
                {ERROR_STRING.APPLICATION_SAVE_ERROR}
              </div>
            )}
          </div>
        </div>

        <DocumentViewer
          fileData={documentViewerDocument}
          isOpen={openDocumentViewer}
          fileName={selectedFile?.filename}
          handleClose={() => setOpenDocumentViewer(false)}
        />
      </div>
    </>
  );
};